import { useGsapStagger } from '../../hooks/useGsapReveal'
import Section from '../ui/Section'
import { CmsHeading } from '../../cms/CmsHeading'
import { useSection } from '../../cms/cms-context'
import { cmsIcon } from '../../cms/icons'

/* The six reasons, in a grid.
 *
 * The list below is the one this section shipped with, and it is still what
 * renders when nothing has been changed and when the API cannot be reached.
 * The console lays its own over the top: reordered, switched off one at a
 * time, rewritten, or added to. */
const REASONS = [
  {
    id: 'verified',
    content: {
      icon: 'BadgeCheck',
      title: 'Verified vehicles',
      body: 'Every car is brand new, sourced through authorised channels, and checked before it reaches the floor.',
    },
  },
  {
    id: 'pricing',
    content: {
      icon: 'ReceiptText',
      title: 'Transparent pricing',
      body: 'The markup, the processing fee and the total payable over the term are all written down before you sign.',
    },
  },
  {
    id: 'financing',
    content: {
      icon: 'Banknote',
      title: 'Flexible financing',
      body: 'Down payments from 20 per cent and tenures from three to seven years, structured around what you can carry monthly.',
    },
  },
  {
    id: 'experience',
    content: {
      icon: 'Sparkles',
      title: 'Premium experience',
      body: 'A lounge rather than a lot. Come in, sit down, and go through the numbers without anyone hovering.',
    },
  },
  {
    id: 'banks',
    content: {
      icon: 'Landmark',
      title: 'Trusted banking partners',
      body: 'Eight banks, Islamic and conventional, competing for your file instead of you chasing each one.',
    },
  },
  {
    id: 'support',
    content: {
      icon: 'Headphones',
      title: 'Professional support',
      body: 'One person owns your case from first enquiry to delivery, and answers the phone after it too.',
    },
  },
]

function WhyChooseUs() {
  const cms = useSection()
  const reasons = cms.items(REASONS)

  const ref = useGsapStagger<HTMLUListElement>('[data-reason]', {
    y: 26,
    stagger: 0.07,
  })

  return (
    <Section tone="surface" labelledBy="why-title">
      <CmsHeading
        id="why-title"
        eyebrow="Why drive with us"
        title="The part most dealerships leave out"
        lede="Anyone can hand you keys. The difference is in the paperwork, the markup, and who picks up when something goes wrong."
      />

      <ul
        ref={ref}
        className="mt-14 grid gap-px overflow-hidden rounded-xl border border-line bg-line shadow-card sm:grid-cols-2 lg:grid-cols-3"
      >
        {reasons.map((reason) => {
          const Icon = cmsIcon(reason.content.icon)
          return (
            <li
              key={reason.id}
              data-reason
              {...reason.props}
              className="group flex flex-col gap-4 bg-raised p-8 transition-colors duration-300 hover:bg-surface"
            >
              <span className="icon-ring size-12 transition duration-300 group-hover:bg-lime group-hover:text-void">
                <Icon aria-hidden="true" strokeWidth={1.6} className="size-5" />
              </span>
              <h3 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-platinum">
                {reason.content.title}
              </h3>
              <p className="text-[14.5px] leading-relaxed text-steel">{reason.content.body}</p>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}

export default WhyChooseUs
